import { useCallback } from 'react';

import { ROUTES } from '../../../constants';
import { widgetSDK } from '../../sdk/widgetSDK';
import { renderModel } from '../../util/renderModel';

const CHATBOT_LOGIN_STORAGE_KEY = 'wix_chatbot:post_login';

const { locale, wixBrand, queryParams, visitorId = '' } = renderModel.get();

const ORIGIN = window.location.origin;

const wdp = queryParams['x-wix-route'];
const postLoginUrl = `${ORIGIN}${ROUTES.CONTACT}${ROUTES.POST_LOGIN}?visitorId=${visitorId}&x-wix-route=${wdp}`;

/**
 * Login Dialog - https://github.com/wix-private/users-fed/tree/master/login-statics
 * originUrl: url that the user will get if he clicks on the X
 * postLogin: url that the user will be redirected to after login
 */
const generateLoginLink = () => {
  const urlSearchParams = new URLSearchParams({
    referralInfo: 'wix-chatbot-client',
    loginDialogContext: 'login',
    overrideLocale: locale,
    originUrl: `${postLoginUrl}&isSuccess=false`,
    postLogin: `${postLoginUrl}&isSuccess=true`,
  });
  return `https://users.${wixBrand}.com/signin?${urlSearchParams.toString()}`;
};

/**
 * If we're logged in on chatbot page - we just reload the whole page
 * If we're logged in on chatbot widget - we refetch widget initial data and initial state
 */
const useHandleLogin = () => {
  return useCallback(async () => {
    if (!renderModel.isFullWidth()) {
      await widgetSDK.refreshOnUserLogin();
    }

    window.location.reload();
  }, []);
};

/**
 * The useLogin hook returns a function for handle login button (see LoginSelector.tsx)
 * On click it does two things:
 * 1. open login wix page
 * 2. listen to storage event to check if post_login key has been set
 * When event is triggered we clear storage and refresh app
 */
export const useLogin = () => {
  const handleLogin = useHandleLogin();
  const createPostLoginStorageListener = (tab: Window | null) => {
    const handleUserLogin = () => {
      tab?.close();
      removePostLoginStorageListener();
      localStorage.removeItem(CHATBOT_LOGIN_STORAGE_KEY);
      handleLogin();
    };

    const removePostLoginStorageListener = () => {
      window.removeEventListener('storage', postLoginStorageListener);
    };

    const postLoginStorageListener = (event: StorageEvent) => {
      if (event.key === CHATBOT_LOGIN_STORAGE_KEY) {
        if (event.newValue) {
          handleUserLogin();
        }
      }
    };
    return postLoginStorageListener;
  };

  return useCallback(() => {
    const link = generateLoginLink();
    const tab = window.open(link, 'signin-window');
    const postLoginStorageListener = createPostLoginStorageListener(tab);
    window.addEventListener('storage', postLoginStorageListener);
  }, [createPostLoginStorageListener]);
};
